import type {CreateBatchTaskInput, Duration, RecurringTask} from '../model';
import {DurationType, PartOfTheDay} from '../model';
import {
  getDateFromIso,
  getIsoDateFromDateAndTime,
  getStartDateAndEndDateForWorkingDay,
  getTimeFromIso,
  isTaskBeforeMillisForShiftTimestampEnd,
} from './timeHelpers';
import {
  getUTCDateTimeFromMillis,
  getUTCDateTimeFromString,
} from './getUTCDateTime';
import {DateTime} from 'luxon';
import type {DurationUnit} from 'luxon';
import {TASK_RETENTION_IN_MONTHS} from './taskArchive';

export const DEFAULT_TIME_ZONE = 'Europe/Warsaw';

const MAX_DRIFT_ACROSS_MIDNIGHT_IN_HOURS = 2;

const INTERVAL_UNITS: Record<DurationType, DurationUnit> = {
  [DurationType.HOUR]: 'hours',
  [DurationType.DAY]: 'days',
  [DurationType.WEEK]: 'weeks',
};

type RecurringSchedule = {
  startTimestamp: string;
  timeZone?: string | null;
  completeUntil?: {time?: string | null} | null;
};

export const generateTasksToCreate = (
  recurringTask: RecurringTask,
  shiftHour: number
): {
  tasksToCreate: CreateBatchTaskInput[];
  lastOccurrence: number | undefined;
} => {
  const tasksToCreate: CreateBatchTaskInput[] = [];
  const {
    id,
    lastOccurrenceTimestamp,
    interval,
    startTimestamp,
    content,
    title,
    occurrences,
    currentOccurrence,
    assignedToResident,
    assignedToEmployee,
  } = recurringTask;
  let thereIsTaskToBeCreated = true;
  let lastOccurrence = lastOccurrenceTimestamp;
  console.info('Generating tasks for recurring parent');
  const {shiftEndTimeMillis} = getStartDateAndEndDateForWorkingDay(shiftHour);
  while (thereIsTaskToBeCreated) {
    const nextOccurrenceTimestamp = lastOccurrence
      ? calculateNextOccurrence(lastOccurrence, interval, recurringTask)
      : getUTCDateTimeFromString(startTimestamp).toMillis();
    if (
      isTaskBeforeMillisForShiftTimestampEnd(
        nextOccurrenceTimestamp,
        shiftEndTimeMillis
      ) &&
      (occurrences < 0 ||
        tasksToCreate.length + currentOccurrence < occurrences)
    ) {
      tasksToCreate.push({
        date: getDateFromIso(
          getUTCDateTimeFromMillis(nextOccurrenceTimestamp).toISO()
        ),
        content,
        title,
        assignedToResident,
        assignedToEmployee,
        completeUntil: {
          time: recurringTask.completeUntil?.time
            ? getTimeFromIso(
                getUTCDateTimeFromMillis(nextOccurrenceTimestamp).toISO()
              )
            : undefined,
          partOfTheDay: recurringTask.completeUntil?.time
            ? getPartOfTheDayFromTimestamp(nextOccurrenceTimestamp)
            : recurringTask.completeUntil?.partOfTheDay,
        },
        parentRecurringTaskId: id,
      });
      lastOccurrence = nextOccurrenceTimestamp;
    } else {
      thereIsTaskToBeCreated = false;
    }
  }
  return {tasksToCreate, lastOccurrence};
};

export const calculateNextOccurrence = (
  lastOccurrence: number,
  {type, amount}: Duration,
  {startTimestamp, timeZone, completeUntil}: RecurringSchedule
): number => {
  const unit = INTERVAL_UNITS[type];
  if (!unit) {
    console.error('No interval type provided!');
    throw new Error('No interval type provided');
  }
  const isScheduledAtTimeOfDay =
    type !== DurationType.HOUR && !!completeUntil?.time;
  if (!isScheduledAtTimeOfDay) {
    return DateTime.fromMillis(lastOccurrence, {zone: 'utc'})
      .plus({[unit]: amount})
      .toMillis();
  }
  const zone = timeZone ?? DEFAULT_TIME_ZONE;
  const next = DateTime.fromMillis(lastOccurrence, {zone}).plus({
    [unit]: amount,
  });
  const {hour, minute} = DateTime.fromISO(startTimestamp, {zone});
  const hoursFromStartTime = next.diff(next.set({hour, minute}), 'hours').hours;
  const daysAcrossMidnight =
    Math.abs(hoursFromStartTime) > 24 - MAX_DRIFT_ACROSS_MIDNIGHT_IN_HOURS
      ? Math.sign(hoursFromStartTime)
      : 0;
  return next.plus({days: daysAcrossMidnight}).set({hour, minute}).toMillis();
};

export const isOnStartDay = (
  timestamp: string,
  {startTimestamp, timeZone}: RecurringSchedule
): boolean => {
  const zone = timeZone ?? DEFAULT_TIME_ZONE;
  return DateTime.fromISO(timestamp, {zone}).hasSame(
    DateTime.fromISO(startTimestamp, {zone}),
    'day'
  );
};

export const getPartOfTheDayFromDateAndTime = (
  time: string,
  date: string
): PartOfTheDay => {
  return getPartOfTheDayFromDateObject(
    getUTCDateTimeFromString(getIsoDateFromDateAndTime(date, time))
  );
};

export const getPartOfTheDayFromTimestamp = (time: number): PartOfTheDay => {
  return getPartOfTheDayFromDateObject(getUTCDateTimeFromMillis(time));
};

const getPartOfTheDayFromDateObject = (date: DateTime): PartOfTheDay => {
  const timestampHour = date.hour;
  if (timestampHour >= 18 || timestampHour < 5) return PartOfTheDay.NIGHT;
  if (timestampHour >= 5 && timestampHour < 10) return PartOfTheDay.MORNING;
  if (timestampHour >= 10 && timestampHour < 14) return PartOfTheDay.AFTERNOON;
  if (timestampHour >= 14 && timestampHour < 18) return PartOfTheDay.EVENING;
  console.warn('Could not resolve timestamp to Part of the day!', {
    date: JSON.stringify(date),
    timestampHour,
  });
  throw new Error('Timestamp could not be resolved to valid part of the day !');
};

export const getTaskExpiryTimestamp = (date: string | number): number => {
  const isDate = String(date).includes('-');
  let timestamp;
  if (isDate) {
    timestamp = getUTCDateTimeFromString(date as string);
  } else {
    timestamp = getUTCDateTimeFromMillis(date as number);
  }
  return timestamp.plus({months: TASK_RETENTION_IN_MONTHS}).toSeconds();
};
